/** Display helpers for platform / org models (lab · route · family). */

export type ModelDisplaySource = {
  productId: string;
  displayName?: string;
  provider?: string;
  lab?: string;
  route?: string;
  family?: string;
  chatCapable?: boolean;
  modelKey?: string;
  wayLabel?: string;
};

const FAMILY_LABELS: Record<string, string> = {
  gpt: 'GPT',
  claude: 'Claude',
  nova: 'Nova',
  embed: 'Embeddings',
  other: 'Other',
};

const LAB_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  amazon: 'Amazon',
  cohere: 'Cohere',
  twelvelabs: 'TwelveLabs',
  xai: 'xAI',
  deepseek: 'DeepSeek',
  moonshot: 'Moonshot AI',
  mistral: 'Mistral',
  qwen: 'Qwen',
  zai: 'Z.AI',
};

const ROUTE_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  bedrock: 'Bedrock',
};

export function familyLabel(family?: string): string {
  if (!family) return 'Other';
  return FAMILY_LABELS[family] || family.charAt(0).toUpperCase() + family.slice(1);
}

export function labLabel(lab?: string): string {
  if (!lab) return '';
  return LAB_LABELS[lab] || lab.charAt(0).toUpperCase() + lab.slice(1);
}

export function routeLabel(route?: string, providerFallback?: string): string {
  const r = route || providerFallback || '';
  if (!r) return '';
  return ROUTE_LABELS[r] || r.charAt(0).toUpperCase() + r.slice(1);
}

export function modelRoute(m: ModelDisplaySource): string {
  return (m.route || m.provider || '').toLowerCase();
}

export function modelLab(m: ModelDisplaySource): string {
  return (m.lab || '').toLowerCase();
}

/** Dual badge text, or single chip when route === lab (e.g. OpenAI). */
export function routeLabBadgeText(m: ModelDisplaySource): { route: string; lab: string; collapsed: boolean } {
  const route = routeLabel(m.route, m.provider);
  const lab = labLabel(m.lab);
  const routeKey = modelRoute(m);
  const labKey = modelLab(m);
  const collapsed = Boolean(routeKey && labKey && routeKey === labKey);
  return { route, lab, collapsed };
}

export function formatModelOptionLabel(m: ModelDisplaySource): string {
  const { route, lab, collapsed } = routeLabBadgeText(m);
  const name = m.displayName || m.productId;
  if (collapsed && route) return `${name} · ${route}`;
  if (route && lab) return `${name} · ${route} · ${lab}`;
  if (route) return `${name} · ${route}`;
  return name;
}

export function matchesModelSearch(m: ModelDisplaySource, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystacks = [
    m.displayName,
    m.productId,
    m.lab,
    m.route,
    m.family,
    m.provider,
    labLabel(m.lab),
    routeLabel(m.route, m.provider),
    familyLabel(m.family),
  ];
  return haystacks.some((h) => (h || '').toLowerCase().includes(q));
}

export const FAMILY_ORDER = ['gpt', 'claude', 'nova', 'embed', 'other'] as const;

export const LAB_ORDER = [
  'openai',
  'anthropic',
  'amazon',
  'xai',
  'deepseek',
  'moonshot',
  'mistral',
  'qwen',
  'zai',
  'cohere',
  'twelvelabs',
] as const;

/** One line under the display name: lab, route, product id. */
export function modelMetaLine(m: ModelDisplaySource): string {
  const { route, lab, collapsed } = routeLabBadgeText(m);
  const parts: string[] = [];
  if (collapsed && route) parts.push(route);
  else {
    if (lab) parts.push(lab);
    if (route) parts.push(route);
  }
  if (m.productId) parts.push(m.productId);
  return parts.join(' · ');
}

export function modelSupportsEffort(capabilities?: string[]): boolean {
  return Boolean(capabilities?.includes('reasoning/effort'));
}

export function groupModelsByFamily<T extends ModelDisplaySource>(
  models: T[]
): { family: string; label: string; models: T[] }[] {
  const buckets = new Map<string, T[]>();
  for (const m of models) {
    const key = m.family || 'other';
    const list = buckets.get(key) || [];
    list.push(m);
    buckets.set(key, list);
  }
  const ordered: { family: string; label: string; models: T[] }[] = [];
  for (const fam of FAMILY_ORDER) {
    const list = buckets.get(fam);
    if (list?.length) {
      ordered.push({ family: fam, label: familyLabel(fam), models: list });
      buckets.delete(fam);
    }
  }
  for (const [fam, list] of buckets) {
    if (list.length) {
      ordered.push({ family: fam, label: familyLabel(fam), models: list });
    }
  }
  return ordered;
}

export function groupModelsByLab<T extends ModelDisplaySource>(
  models: T[]
): { lab: string; label: string; models: T[] }[] {
  const buckets = new Map<string, T[]>();
  for (const m of models) {
    const key = modelLab(m) || 'other';
    const list = buckets.get(key) || [];
    list.push(m);
    buckets.set(key, list);
  }
  const ordered: { lab: string; label: string; models: T[] }[] = [];
  for (const lab of LAB_ORDER) {
    const list = buckets.get(lab);
    if (list?.length) {
      ordered.push({ lab, label: labLabel(lab), models: list });
      buckets.delete(lab);
    }
  }
  for (const [lab, list] of buckets) {
    if (list.length) {
      ordered.push({ lab, label: labLabel(lab), models: list });
    }
  }
  return ordered;
}
